<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue';
import LockIcon from '@/assets/icons/core/lock.svg';
import { useMaxLength } from '@/modules/ui/composables/use-max-length.ts';
import { useFocusControl } from '@/modules/ui/composables/use-focus-control.ts';
import { useTextareaAutoGrow } from '@/modules/ui/composables/use-textarea-auto-grow.ts';
import { truncateToLimit, usePasteOverflow } from '@/modules/ui/composables/use-paste-overflow.ts';
import { uniqueId } from 'lodash-es';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import PasteOverflowModal from '@/modules/ui/components/common/PasteOverflowModal.vue';
import { getAutocorrectInsertion } from '@/modules/common/utils/autocorrect-insertion.util.ts';

const model = defineModel<string>({ required: true, default: '' });

const {
  id = uniqueId(IdPrefix.Control),
  placeholder = 'ui.textarea.enterText',
  disabled,
  readonly,
  error,
  rows = 4,
  maxLength,
  noLineBreaks,
  noResize,
  autoGrow = false,
  autoGrowMinHeight,
  autoGrowMaxRows,
} = defineProps<{
  id?: string;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  rows?: number;
  autocomplete?: string;
  maxLength?: number;
  focusOnMount?: boolean;
  noResize?: boolean;
  noLineBreaks?: boolean;
  autoGrow?: boolean;
  autoGrowMinHeight?: number;
  autoGrowMaxRows?: number;
}>();

const emit = defineEmits<{
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
}>();

const textareaRef = useTemplateRef('textarea');
const fieldRef = useTemplateRef('fieldRef');
let hasCaret = false;
let savedSelection: { start: number; end: number } | null = null;

const { isFocused, onFieldMousedown, onFieldClick, onFocus, onBlur } = useFocusControl(model, textareaRef, emit);

watch(model, () => {
  if (document.activeElement !== textareaRef.value) {
    hasCaret = false;
    savedSelection = null;
  }
});

function onTextareaFocus(event: FocusEvent) {
  hasCaret = true;
  savedSelection = null;
  onFocus(event);
}

function onTextareaBlur(event: FocusEvent) {
  const el = textareaRef.value;

  if (el && hasCaret) {
    savedSelection = { start: el.selectionStart, end: el.selectionEnd };
  }

  onBlur(event);
}

function resetInsertionSelection() {
  hasCaret = false;
  savedSelection = null;
}

function insertText(text: string): boolean {
  const el = textareaRef.value;

  if (!el || disabled || readonly) {
    return false;
  }

  const source = el.value;
  const selection =
    document.activeElement === el && hasCaret ? { start: el.selectionStart, end: el.selectionEnd } : savedSelection;
  const start = Math.min(selection?.start ?? source.length, source.length);
  const end = Math.min(selection?.end ?? source.length, source.length);
  const value = noLineBreaks ? text.replace(/[\r\n]+/g, ' ') : text;
  const insertion = getAutocorrectInsertion(value, source.slice(0, start), source.slice(end));

  if (!insertion) {
    return false;
  }

  const result = source.slice(0, start) + insertion + source.slice(end);

  if (maxLength !== undefined && result.length > maxLength) {
    return false;
  }

  el.focus();
  el.setRangeText(insertion, start, end, 'end');
  const caret = start + insertion.length;
  el.setSelectionRange(caret, caret);
  model.value = el.value;
  hasCaret = true;
  savedSelection = null;

  void nextTick(() => {
    if (el.value === result && document.activeElement === el) {
      el.setSelectionRange(caret, caret);
    }
  });

  return true;
}

const { gradient, onInput } = useMaxLength(
  model,
  computed(() => maxLength)
);

const fieldClassName = computed(() => ({
  '_focused': !!isFocused.value,
  '_disabled': disabled,
  '_readonly': !!readonly,
  '_error': error,
  '_auto-grow': autoGrow,
}));

const { adjustHeight } = useTextareaAutoGrow(textareaRef, fieldRef, {
  autoGrow,
  autoGrowMinHeight,
  autoGrowMaxRows,
  model,
});

function onTextareaInput(event: Event) {
  onInput(event);
  adjustHeight();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && noLineBreaks) {
    event.preventDefault();
  } else {
    emit('keydown', event);
  }
}

const {
  isOpen: isTruncateConfirmOpen,
  info: pasteOverflow,
  confirm: confirmPasteOverflow,
  accept: acceptPasteOverflow,
} = usePasteOverflow();

async function onPaste(event: ClipboardEvent) {
  const el = textareaRef.value;
  if (!el) {
    return;
  }

  const pasted = event.clipboardData?.getData('text') ?? '';
  const sanitized = noLineBreaks ? pasted.replace(/[\r\n]+/g, ' ') : pasted;
  const value = el.value.slice(0, el.selectionStart) + sanitized + el.value.slice(el.selectionEnd);

  if (maxLength && value.length > maxLength) {
    event.preventDefault();

    const accepted = await confirmPasteOverflow({
      pastedLength: sanitized.length,
      resultLength: value.length,
      limit: maxLength,
    });

    if (accepted) {
      model.value = truncateToLimit(value, maxLength);
    }
  } else if (noLineBreaks) {
    event.preventDefault();
    model.value = value;
  }
}

defineExpose({
  isFocused,
  focus: () => textareaRef.value?.focus(),
  blur: () => textareaRef.value?.blur(),
  insertText,
  resetInsertionSelection,
});
</script>

<template>
  <div
    ref="fieldRef"
    class="ui-textarea"
    :class="fieldClassName"
    @mousedown="onFieldMousedown"
    @click="onFieldClick"
  >
    <div class="content">
      <textarea
        :id
        ref="textarea"
        v-focus="focusOnMount"
        class="textarea"
        :class="{ '_no-resize': noResize }"
        :style="autoGrowMinHeight != null ? { minHeight: `${autoGrowMinHeight}px` } : undefined"
        :rows
        :autocomplete
        :value="model"
        :disabled
        :readonly="!!readonly"
        @input="onTextareaInput"
        @focus="onTextareaFocus"
        @blur="onTextareaBlur"
        @keydown="onKeydown"
        @paste="onPaste"
      />
      <div
        v-if="!model && placeholder"
        class="placeholder"
      >
        {{ $maybeT(placeholder) }} {{ maxLength ? `(${maxLength})` : '' }}
      </div>
    </div>

    <span
      v-if="readonly"
      class="lock"
    >
      <LockIcon class="lock-icon" />
    </span>

    <slot />

    <PasteOverflowModal
      v-model="isTruncateConfirmOpen"
      :length="pasteOverflow?.resultLength ?? 0"
      :max="pasteOverflow?.limit ?? 0"
      @confirm="acceptPasteOverflow"
    />
  </div>
</template>

<style scoped lang="scss">
.ui-textarea {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: text;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  background: var(--color-gray-075)
    linear-gradient(
      to right,
      rgb(226 231 239 / 100%) 0%,
      rgb(226 231 239 / 100%) v-bind('gradient.start'),
      transparent v-bind('gradient.end'),
      transparent 100%
    );
  transition:
    box-shadow var(--transition-fast),
    background-color var(--transition-fast);

  &:not(._error) {
    &._focused {
      box-shadow: var(--shadow-control);
    }
  }

  &._disabled {
    pointer-events: none;
    background-image: none;
    background-color: var(--color-gray-075);

    .content {
      opacity: 0.5;
    }
  }

  &._readonly {
    background-image: none;
    background-color: var(--color-gray-050);
    box-shadow: none;
  }

  &._error {
    background: var(--color-red-100)
      linear-gradient(
        to right,
        var(--color-red-250) 0%,
        var(--color-red-250) v-bind('gradient.start'),
        transparent v-bind('gradient.end'),
        transparent 100%
      );

    &._focused {
      box-shadow: var(--shadow-control-error);
    }

    &._disabled,
    &._readonly {
      background-image: none;
      background-color: var(--color-red-100);
    }

    .textarea {
      color: var(--color-red-800);
    }

    .placeholder {
      color: var(--color-red-800);
    }
  }

  &._auto-grow {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    flex: 0 1 auto;
    align-self: stretch;
    max-height: 100%;
    height: auto;

    .content {
      display: block;
      width: 100%;
      flex: none;
      min-height: 0;
      max-height: none;
    }

    .textarea {
      display: block;
      width: 100%;
      box-sizing: border-box;
      background: transparent;
      overflow-y: hidden;
    }
  }
}

.content {
  position: relative;
  display: flex;
  flex-grow: 1;
}

.lock {
  display: flex;
  flex-shrink: 0;
  margin-left: 8px;
  align-items: flex-start;
  padding-top: 2px;
}

.lock-icon {
  width: 16px;
  height: 16px;
  fill: var(--color-gray-600);
}

.textarea {
  flex-grow: 1;
  min-height: 36px;
  resize: vertical;
  transition: color var(--transition-fast);

  &._no-resize {
    resize: none;
  }
}

.placeholder {
  position: absolute;
  bottom: 0;
  left: 0;
  pointer-events: none;
  color: var(--color-gray-500);
  transition: color var(--transition-fast);
}
</style>
